import { prodDb, subDb, localDataDb } from '../config/pouch';

// Vérification de la connectivité
export const testNetworkConnectivity = async (timeoutMs = 5000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetch('https://www.google.com/favicon.ico', {
            mode: 'no-cors',
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        return true;
    } catch (error) {
        return false;
    }
};

// Récupération des produits en mode hors ligne avec recherche avancée
export const getAllProductsOffline = async (filters = {}) => {
    try {
        const result = await prodDb.allDocs({
            include_docs: true
        });

        let products = result.rows.map(row => row.doc);

        // Fonction helper pour recherche multi-termes
        const containsAllWords = (text, searchText) => {
            if (!text || !searchText) return false;
            const lowerText = text.toLowerCase();
            const words = searchText.toLowerCase().split(/\s+/).filter(w => w.length > 0);
            return words.every(word => lowerText.includes(word));
        };

        // Appliquer les filtres si fournis
        if (filters.search) {
            const searchTerm = filters.search;
            products = products.filter(product => {
                const nom = product.nom || '';
                const nomCommercial = product.nomCommercial || '';
                const dci = product.DCI || product.dci || '';
                const laboratoire = product.laboratoire || product.fabricant || '';
                const forme = product.forme || '';

                // Support recherche multi-termes (ex: "PARACÉTAMOL CODÉINE")
                return containsAllWords(nom, searchTerm) ||
                    containsAllWords(nomCommercial, searchTerm) ||
                    containsAllWords(dci, searchTerm) ||
                    containsAllWords(laboratoire, searchTerm) ||
                    containsAllWords(forme, searchTerm);
            });
        }

        // Appliquer les filtres spécifiques
        if (filters.nom) {
            products = products.filter(p =>
                containsAllWords(p.nom, filters.nom) ||
                containsAllWords(p.nomCommercial, filters.nom)
            );
        }

        if (filters.DCI) {
            products = products.filter(p =>
                containsAllWords(p.DCI || p.dci || '', filters.DCI)
            );
        }

        if (filters.laboratoire) {
            products = products.filter(p =>
                containsAllWords(p.laboratoire || p.fabricant || '', filters.laboratoire)
            );
        }

        // Limiter les résultats si demandé
        if (filters.limit) {
            products = products.slice(0, filters.limit);
        }

        return products;

    } catch (error) {
        return [];
    }
};

// Récupération des substances en mode hors ligne avec recherche avancée
export const getAllSubstancesOffline = async (filters = {}) => {
    try {
        const result = await subDb.allDocs({
            include_docs: true
        });

        let substances = result.rows.map(row => row.doc);

        // Appliquer les filtres si fournis
        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            substances = substances.filter(substance => {
                const name = (substance.name || substance.nom || '').toLowerCase();
                const description = (substance.description || '').toLowerCase();
                const category = (substance.category || substance.categorie || '').toLowerCase();

                return name.includes(searchTerm) ||
                    description.includes(searchTerm) ||
                    category.includes(searchTerm);
            });
        }

        // Appliquer les filtres spécifiques
        if (filters.name) {
            const nameFilter = filters.name.toLowerCase();
            substances = substances.filter(s => (s.name || s.nom || '').toLowerCase().includes(nameFilter));
        }

        // Limiter les résultats si demandé
        if (filters.limit) {
            substances = substances.slice(0, filters.limit);
        }

        return substances;

    } catch (error) {
        return [];
    }
};

// Fonction générique pour les requêtes avec support hors ligne
export const fetchWithOfflineSupport = async (onlineFetch, offlineFetch) => {
    const isOnline = await testNetworkConnectivity();

    try {
        if (isOnline) {
            const data = await onlineFetch();
            return {
                data,
                source: 'online'
            };
        }
    } catch (error) {}

    try {
        const data = await offlineFetch();
        return {
            data,
            source: 'offline'
        };
    } catch (error) {
        throw error;
    }
};

// Récupération des équivalences en mode hors ligne
export const getAllEquivalencesOffline = async (filters = {}) => {
    try {
        const result = await localDataDb.allDocs({
            include_docs: true,
            startkey: 'equiv_',
            endkey: 'equiv_\uffff'
        });

        let equivalences = result.rows.map(row => row.doc);

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            equivalences = equivalences.filter(eq => {
                return Object.values(eq).some(value =>
                    typeof value === 'string' && value.toLowerCase().includes(searchTerm)
                );
            });
        }

        if (filters.limit) {
            equivalences = equivalences.slice(0, filters.limit);
        }

        return equivalences;

    } catch (error) {
        return [];
    }
};

// Récupération des interactions en mode hors ligne
export const getAllInteractionsOffline = async (filters = {}) => {
    try {
        const result = await localDataDb.allDocs({
            include_docs: true,
            startkey: 'inter_',
            endkey: 'inter_\uffff'
        });

        let interactions = result.rows.map(row => row.doc);

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            interactions = interactions.filter(inter => {
                return Object.values(inter).some(value =>
                    typeof value === 'string' && value.toLowerCase().includes(searchTerm)
                );
            });
        }

        if (filters.limit) {
            interactions = interactions.slice(0, filters.limit);
        }

        return interactions;

    } catch (error) {
        return [];
    }
};

// Récupération des posologies en mode hors ligne
export const getAllPosologiesOffline = async (filters = {}) => {
    try {
        const result = await localDataDb.allDocs({
            include_docs: true,
            startkey: 'poso_',
            endkey: 'poso_\uffff'
        });

        let posologies = result.rows.map(row => row.doc);

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            posologies = posologies.filter(poso => {
                return Object.values(poso).some(value =>
                    typeof value === 'string' && value.toLowerCase().includes(searchTerm)
                );
            });
        }

        if (filters.limit) {
            posologies = posologies.slice(0, filters.limit);
        }

        return posologies;

    } catch (error) {
        return [];
    }
};

// Récupération des laboratoires en mode hors ligne
export const getAllLaboratoiresOffline = async (filters = {}) => {
    try {
        const result = await localDataDb.allDocs({
            include_docs: true,
            startkey: 'lab_',
            endkey: 'lab_\uffff'
        });

        let laboratoires = result.rows.map(row => row.doc);

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            laboratoires = laboratoires.filter(lab => {
                return Object.values(lab).some(value =>
                    typeof value === 'string' && value.toLowerCase().includes(searchTerm)
                );
            });
        }

        if (filters.limit) {
            laboratoires = laboratoires.slice(0, filters.limit);
        }

        return laboratoires;

    } catch (error) {
        return [];
    }
};

// Récupération des données JSON locales (classes, dosages, formes, etc.)
export const getLocalJsonData = async (collection) => {
    try {
        const doc = await localDataDb.get(`json_${collection}`);
        return doc.data || [];
    } catch (error) {
        return [];
    }
};

// Recherche globale dans toutes les données
export const searchAllDataOffline = async (searchTerm, limit = 50) => {
    try {
        const [products, substances, equivalences, interactions, posologies, laboratoires] = await Promise.all([
            getAllProductsOffline({ search: searchTerm, limit: limit / 6 }),
            getAllSubstancesOffline({ search: searchTerm, limit: limit / 6 }),
            getAllEquivalencesOffline({ search: searchTerm, limit: limit / 6 }),
            getAllInteractionsOffline({ search: searchTerm, limit: limit / 6 }),
            getAllPosologiesOffline({ search: searchTerm, limit: limit / 6 }),
            getAllLaboratoiresOffline({ search: searchTerm, limit: limit / 6 })
        ]);

        return {
            products,
            substances,
            equivalences,
            interactions,
            posologies,
            laboratoires,
            total: products.length + substances.length + equivalences.length +
                interactions.length + posologies.length + laboratoires.length
        };

    } catch (error) {
        return {
            products: [],
            substances: [],
            equivalences: [],
            interactions: [],
            posologies: [],
            laboratoires: [],
            total: 0
        };
    }
};

export const forceConnectivityCheck = async (timeoutMs = 5000) => {
    return await testNetworkConnectivity(timeoutMs);
};

// Récupération des profils en mode hors ligne
export const getAllProfilesOffline = async (filters = {}) => {
    try {
        const result = await localDataDb.allDocs({
            include_docs: true
        });

        let profiles = result.rows
            .map(row => row.doc)
            .filter(doc => doc.type === 'profile' || doc.profile || doc.nom);

        // Appliquer les filtres si fournis
        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            profiles = profiles.filter(profile => {
                const name = (profile.name || profile.nom || profile.profile || '').toLowerCase();
                const description = (profile.description || '').toLowerCase();
                return name.includes(searchTerm) || description.includes(searchTerm);
            });
        }

        // Limiter les résultats si demandé
        if (filters.limit) {
            profiles = profiles.slice(0, filters.limit);
        }

        return profiles;

    } catch (error) {
        return [];
    }
};

// Recherche de produits en mode hors ligne (alias pour getAllProductsOffline avec recherche)
export const searchProductsOffline = async (searchTerm, options = {}) => {
    const filters = {
        search: searchTerm,
        ...options
    };
    return await getAllProductsOffline(filters);
};

// Récupération d'une substance par ID en mode hors ligne
export const getSubstanceByIdOffline = async (substanceId) => {
    try {
        const doc = await subDb.get(substanceId);
        return doc;
    } catch (error) {
        // Si la substance n'est pas trouvée par ID direct, essayer de la chercher par nom ou autres champs
        try {
            const result = await subDb.allDocs({
                include_docs: true
            });

            const substance = result.rows
                .map(row => row.doc)
                .find(doc =>
                    doc._id === substanceId ||
                    doc.id === substanceId ||
                    doc.name === substanceId ||
                    doc.nom === substanceId
                );

            if (substance) {
                return substance;
            }
        } catch (searchError) {

        }

        throw new Error(`Substance avec ID ${substanceId} non trouvée en mode hors ligne`);
    }
};

